import { i18n } from '@lingui/core';
import { cva } from 'class-variance-authority';
import { type InputHTMLAttributes, useCallback, useMemo, useRef, useState } from 'react';

import { Text } from '@/components/typography';
import { getWeekdayNames } from '@/helper/dateHelper';
import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';

type ValidationStatus = 'error' | 'warning';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'type' | 'value'> & {
  className?: string;
  defaultValue?: string[];
  value?: string[];
  selectionMode?: 'single' | 'multiple';
  disabled?: boolean;
  status?: ValidationStatus;
  onChange?: (value?: string[]) => void;
};

const dayChooserDay = cva(
  'flex size-9.5 cursor-pointer items-center justify-center rounded-full border transition-colors duration-300 focus-visible:outline-1',
  {
    variants: {
      status: {
        default:
          'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-100 focus-visible:border-gray-400 focus-visible:bg-gray-100 focus-visible:outline-gray-400',
        error:
          'border-red-400 bg-white text-red-500 hover:border-red-500 hover:bg-gray-100 focus-visible:border-red-500 focus-visible:bg-gray-100 focus-visible:outline-red-400',
        warning:
          'border-yellow-400 bg-white text-yellow-500 hover:border-yellow-500 hover:bg-gray-100 focus-visible:border-yellow-500 focus-visible:bg-gray-100 focus-visible:outline-yellow-400',
      },
      selected: {
        false: '',
        true: '',
      },
      disabled: {
        false: '',
        true: 'cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-200 hover:text-gray-500',
      },
    },
    compoundVariants: [
      {
        status: 'default',
        selected: true,
        disabled: false,
        className:
          'border-green-500 bg-green-400 text-gray-900 hover:border-green-600 hover:bg-green-500',
      },
      {
        status: 'error',
        selected: true,
        disabled: false,
        className:
          'bg-red-200 text-red-700 hover:border-red-600 hover:text-red-700 hover:bg-red-400',
      },
      {
        status: 'warning',
        selected: true,
        disabled: false,
        className:
          'bg-yellow-200 text-yellow-700 hover:border-yellow-600 hover:bg-yellow-400 hover:text-yellow-700',
      },
    ],
    defaultVariants: {
      status: 'default',
      selected: false,
      disabled: false,
    },
  },
);

const DayChooser = ({
  defaultValue,
  value: valueFromProps,
  selectionMode = 'single',
  disabled = false,
  status: statusFromProps,
  onChange: onChangeFromProps,
  ...rest
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [value, onChange] = useControlled(valueFromProps, defaultValue, onChangeFromProps);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(0);
  const status = statusFromProps || (rest['aria-invalid'] === true ? 'error' : undefined);

  const handleItemClick = useCallback(
    (itemValue: string) => {
      if (selectionMode === 'single') {
        onChange([itemValue]);
      }

      if (selectionMode === 'multiple') {
        if ((value || []).includes(itemValue)) {
          onChange((value || []).filter((v) => v !== itemValue));
        } else {
          onChange([...(value || []), itemValue]);
        }
      }
    },
    [value, selectionMode, onChange],
  );

  const days = useMemo(() => getWeekdayNames('narrow'), []);

  return (
    <div
      className="flex gap-4"
      ref={containerRef}
      role="listbox"
      aria-multiselectable={selectionMode === 'multiple' ? true : undefined}
      {...rest}
    >
      {days.map((day, index) => {
        const isSelected = (value || []).includes(index + '');
        const dayLabel = i18n._(day);

        return (
          <Text
            key={index}
            className={cn(
              dayChooserDay({ status: status || 'default', selected: isSelected, disabled }),
            )}
            variant="body2"
            weight="light"
            role="option"
            aria-label={dayLabel}
            aria-selected={isSelected}
            tabIndex={focusedIndex === index && !disabled ? 0 : -1}
            onClick={() => !disabled && handleItemClick(index + '')}
            onFocus={() => !disabled && setFocusedIndex(index)}
            onKeyDown={(e) => {
              e.preventDefault();

              if (containerRef.current == null) {
                return;
              }

              const itemEls = containerRef.current.querySelectorAll<HTMLElement>('[role="option"]');

              switch (e.key) {
                case 'ArrowUp':
                  itemEls[Math.max(index - 1, 0)].focus();
                  setFocusedIndex(Math.max(index - 1, 0));
                  break;
                case 'ArrowDown':
                  itemEls[Math.min(index + 1, days.length - 1)].focus();
                  setFocusedIndex(Math.min(index + 1, days.length - 1));
                  break;
                case 'Home':
                  itemEls[0].focus();
                  setFocusedIndex(0);
                  break;
                case 'End':
                  itemEls[days.length - 1].focus();
                  setFocusedIndex(days.length - 1);
                  break;
                case ' ':
                case 'Enter':
                  handleItemClick(index + '');
                  break;
              }
            }}
          >
            {dayLabel}
          </Text>
        );
      })}
    </div>
  );
};

export default DayChooser;
